import React, { ReactElement } from "react";
import styled from "styled-components";

/**
 * Fresnel's `md` breakpoint. Job pages use the compact layout below this
 * width (xs and sm) and the card layout at and above it.
 */
export const JOB_DETAIL_DESKTOP_MIN_PX = 800;

const DESKTOP = `@media (min-width: ${JOB_DETAIL_DESKTOP_MIN_PX}px)`;

const Frame = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  border-radius: 8px;
  background: #ffffff;
  margin: 8px 0 24px;
  padding: 24px 0;
  box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.12);

  ${DESKTOP} {
    max-width: 930px;
    background: linear-gradient(
      179.99deg,
      #ffffff 0.01%,
      rgba(252, 254, 255, 0.988915) 89.07%,
      rgba(249, 254, 255, 0.976763) 119.2%
    );
    margin: 40px 0;
    padding: 52px 0 40px;
    box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.25);
  }
`;

const Header = styled.header`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
`;

const LogoFrame = styled.div`
  width: 120px;
  height: 120px;
  max-width: calc(100% - 32px);
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;

  ${DESKTOP} {
    width: 141px;
    height: 141px;
  }

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;
    object-position: center;
  }
`;

const Title = styled.h1<{ $withLogo: boolean }>`
  margin: ${(props) => (props.$withLogo ? "12px" : "0")} 0 0;
  padding: 0 16px;
  max-width: 100%;
  box-sizing: border-box;
  font-family: Gilroy, sans-serif;
  font-size: 1.5rem;
  font-weight: 600;
  line-height: 2rem;
  text-align: center;
  overflow-wrap: anywhere;
  color: #292a31;

  ${DESKTOP} {
    margin-top: ${(props) => (props.$withLogo ? "16px" : "0")};
    padding: 0 48px;
  }
`;

const Employer = styled.p`
  margin: 8px 0 0;
  padding: 0 16px;
  max-width: 100%;
  box-sizing: border-box;
  text-align: center;
  font-family: SFProDisplay, sans-serif;
  font-size: 16px;
  font-weight: 500;
  letter-spacing: 0.75px;
  line-height: 28px;
  overflow-wrap: anywhere;
  color: #4d4d4d;

  ${DESKTOP} {
    padding: 0 48px;
  }
`;

const BreakpointBody = styled.div<{ $desktop: boolean }>`
  display: ${(props) => (props.$desktop ? "none" : "flex")};
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;

  ${DESKTOP} {
    display: ${(props) => (props.$desktop ? "flex" : "none")};
  }
`;

interface HeaderProps {
  employerName?: string | null;
  logoUrl?: string | null;
  title?: string | null;
}

export function JobDetailFrame(props: {
  children: React.ReactNode;
}): ReactElement {
  return <Frame>{props.children}</Frame>;
}

export function JobDetailMobileOnly(props: {
  children: React.ReactNode;
}): ReactElement {
  return <BreakpointBody $desktop={false}>{props.children}</BreakpointBody>;
}

export function JobDetailDesktopOnly(props: {
  children: React.ReactNode;
}): ReactElement {
  return <BreakpointBody $desktop={true}>{props.children}</BreakpointBody>;
}

/**
 * Logo, then the single page h1, then the company name.
 * Logo box is 1.5x the previous job-view sizes (80px mobile, 94px desktop).
 */
export default function JobDetailHeader(
  props: HeaderProps
): ReactElement | null {
  const title = props.title?.trim();
  if (!title) {
    return null;
  }
  const logoUrl = props.logoUrl?.trim();
  return (
    <Header>
      {logoUrl ? (
        <LogoFrame>
          <img
            alt=""
            height={120}
            src={logoUrl}
            style={{ objectFit: "contain" }}
            width={120}
          />
        </LogoFrame>
      ) : null}
      <Title $withLogo={Boolean(logoUrl)}>{title}</Title>
      {props.employerName ? <Employer>{props.employerName}</Employer> : null}
    </Header>
  );
}
